/**
 * DEV-3344 (DEV-2603 denetim bulgusu B23) — Sigorta Bilgileri sekmelerinin
 * KAYNAK tablosu.
 *
 * Toplu Haciz talep formunda Banka/EGM/SGK haczi kartları `sorgu-kaynak-rozeti`
 * ile "snapshot · tarih" rozeti ve ücret onaylı Yenile taşırken, Maaş haczi
 * kartındaki "Sigorta Bilgileri" düğmesiyle açılan `BorcluSigortaBilgileri.vue`
 * (7 sekme) hiçbirinde bunu göstermiyordu — kullanıcı aynı formda bir kartta
 * veri yaşını görürken diğerinde görmüyordu.
 *
 * Yedi sekmenin hepsi için "hangi borclu.<alan> nesnesi gösteriliyor, o alan
 * hangi sorguyla tazelenir" bilgisi TEK tabloda toplanıyor. Sayfa mount
 * edilemediği için (suite'te Vue bileşen mount kullanılmıyor — mevcut desen:
 * `sorguKaynagi.ts`) bu eşleme mantığı saf modülde tutulup burada test
 * ediliyor; `.fresh()` çağrısının GERÇEK $uyap isteği ve bileşendeki
 * Vue.set/emit akışı yalnız `BorcluSigortaBilgileri.vue`de, kod okumasıyla
 * doğrulanıyor (rapor: DEV-3344-rapor.md).
 *
 * Alan adları `DosyaBorclu` sınıfının üye adlarıyla BİREBİR aynı tutuldu
 * (`ssk`, `sgkbagkur`, `sgkkamu`, `emekliSsk`, `emekliBagkur`, `emekliKamu`,
 * `sgkIsyeri`) — sorgu sınıflarının `snapshotAlani()` string'i (`sgkssk`,
 * `sgkbagkur`, `sgkkamu`, `emeklissk`, `emeklibagkur`, `sgkisyeri`) farklı bir
 * ad uzayı: o, snapshot deposundaki picker alanı; bu, `borclu` nesnesindeki
 * üye adı. Rozete verilen `kaynak` her iki durumda da KÖK cevap nesnesidir
 * (`ISorguSonuc` şeklinde, `snapshotKaynagi`/`snapshotDamgasi`'i kendi
 * üzerinde taşır) — `sorgu-kaynak-rozeti`'nin zaten Banka kartında okuduğu
 * şekille aynı.
 */

/** `borclu` nesnesinde bu alanların KÖK cevabı (ISorguSonuc şeklinde) tutulur. */
export type SigortaSekmeAlani =
    | "ssk"
    | "sgkbagkur"
    | "sgkkamu"
    | "emekliSsk"
    | "emekliBagkur"
    | "emekliKamu"
    | "sgkIsyeri";

/** Yenile için gereken minimum talep: banka kartındaki `bankaBilgileriniYenile` ile aynı ikili. */
export interface SigortaYenileTalebi {
    dosyaId: string | number;
    kisiKurumId: number;
}

export interface SigortaKaynakTanimi {
    /** `borclu` nesnesindeki alan adı — sekmenin birincil anahtarı. */
    alan: SigortaSekmeAlani;
    /** Onay diyaloğunda ve rozette gösterilecek okunur ad. */
    alanAdi: string;
    /** `borclu` nesnesinden KÖK cevabı okur — rozete VE detay bileşenine verilen nesne budur. */
    cevapSec: (borclu: any) => any;
    /**
     * Yenile: ilgili sorguyu `.fresh()` (forceFresh) ile koşar — hem
     * IndexedDB'yi hem snapshot katmanını atlar, `bankaBilgileriniYenile`
     * deseniyle birebir. `uyap` = bileşenin `this.$uyap` eklentisi.
     */
    sorguKosustur: (uyap: any, talep: SigortaYenileTalebi) => Promise<any>;
}

export const SIGORTA_KAYNAKLARI: SigortaKaynakTanimi[] = [
    {
        alan: "ssk",
        alanAdi: "SSK çalışanı bilgileri",
        cevapSec: (borclu) => borclu?.ssk,
        sorguKosustur: (uyap, talep) => uyap.DosyaBorcluSSK().fresh().run({...talep, sorguBakiye: ""}),
    },
    {
        alan: "sgkbagkur",
        alanAdi: "Bağkur çalışanı bilgileri",
        cevapSec: (borclu) => borclu?.sgkbagkur,
        sorguKosustur: (uyap, talep) => uyap.DosyaBorcluBagkur().fresh().run({...talep, sorguBakiye: ""}),
    },
    {
        alan: "sgkkamu",
        alanAdi: "Kamu çalışanı bilgileri",
        cevapSec: (borclu) => borclu?.sgkkamu,
        sorguKosustur: (uyap, talep) => uyap.SgkKamu().fresh().run({...talep, sorguBakiye: ""}),
    },
    {
        alan: "emekliSsk",
        alanAdi: "SSK emeklisi bilgileri",
        cevapSec: (borclu) => borclu?.emekliSsk,
        sorguKosustur: (uyap, talep) => uyap.SskEmekli().fresh().run(talep),
    },
    {
        alan: "emekliBagkur",
        alanAdi: "Bağkur emeklisi bilgileri",
        cevapSec: (borclu) => borclu?.emekliBagkur,
        sorguKosustur: (uyap, talep) => uyap.BagkurEmekli().fresh().run(talep),
    },
    {
        alan: "emekliKamu",
        alanAdi: "Kamu emeklisi bilgileri",
        cevapSec: (borclu) => borclu?.emekliKamu,
        // DEV-3514 (develop): `DosyaBorclu.queryBorcluEmekliKamu` artık AYRI
        // `KamuEmekli` servisini çağırıyor (önceden `SskEmekli` kopyasıydı).
        // Yenile üretim koduyla aynı sınıfı çağırır.
        sorguKosustur: (uyap, talep) => uyap.KamuEmekli().fresh().run(talep),
    },
    {
        alan: "sgkIsyeri",
        alanAdi: "İşyeri SGK bilgileri",
        cevapSec: (borclu) => borclu?.sgkIsyeri,
        sorguKosustur: (uyap, talep) => uyap.DosyaBorcluIsyeriSgk().fresh().run({...talep, sorguBakiye: ""}),
    },
];

export function sigortaKaynakBul(alan: SigortaSekmeAlani): SigortaKaynakTanimi | undefined {
    return SIGORTA_KAYNAKLARI.find((k) => k.alan === alan);
}

/**
 * Bir alan yenilendiğinde aynı cevabın yazılacağı alanlar (kendisi dahil).
 *
 * DEV-3344'te 'emekliSsk' ve 'emekliKamu' aynı `SskEmekli` isteğini koştuğu için
 * eşleşikti: tek sorgu sonucu iki alana birden yazılıyor, aynı veri için ikinci
 * ücretli istek önleniyordu. DEV-3514 (develop) ile kamu emeklisi ayrı
 * `KamuEmekli` servisine geçti; eşleme kaldırıldı, her alan yalnız kendi
 * cevabını alır. SSK cevabını emekliKamu'ya yazmak Kamu Emeklisi sekmesinde
 * SSK verisi gösterirdi.
 */
export function sigortaEslesikAlanlar(alan: SigortaSekmeAlani): SigortaSekmeAlani[] {
    return [alan];
}

/**
 * DEV-3344 (B23 düzeltme turu 2 — çürütücü bulgusu, orta öncelik): bir sekmenin
 * KÖK cevap nesnesi gerçekten "veri var" sayılır mı? `EmptyObject` (UYAP
 * "kayıt bulunamadı" derse HATA FIRLATMADAN dönen izdüşüm) ve boş obje burada
 * elenir. `BorcluSigortaBilgileri.vue`deki eski `hasValidData` metoduyla
 * BİREBİR aynı kural — saf fonksiyona çıkarıldı ki component mount
 * edilemeden (suite deseni) bu karar burada test edilebilsin.
 */
export function gecerliSorguCevabiMi(cevap: any): boolean {
    if (!cevap) return false;
    if (!cevap.sorguSonucu) return false;
    return true;
}

/**
 * DEV-3344 (B23 düzeltme turu 2 — çürütücü bulgusu x2, orta öncelik): sekme
 * görünürlüğü "sticky ama canlıya duyarlı" olmalı — bir kez görünür olan
 * sekme asla gizlenmez (`||`, önceki `true` hiçbir zaman ezilmez), ama mount
 * anında geçersiz olan bir alan SONRADAN geçerli hâle gelirse (kullanıcı
 * `yenile()`yi kendisi çalıştırsın ya da veri `sigortaEslesikAlanlar`
 * eşleşiği/dış bir yeniden sorgu üzerinden dolaylı gelsin) sekme belirir.
 * İki gerçek senaryo bunu gerektiriyor:
 *   1) emekliSsk/emekliKamu çiftinde `yenile()` aynı taze cevabı HER İKİ
 *      `borclu.<alan>`'a birden yazıyor; mount anında biri geçersizken
 *      diğeri geçerliyse (TopluHacizHazirlaTaskHandler iki alanı AYRI
 *      sıralı UYAP isteğiyle sorguluyor, biri throw edip diğeri dönebilir),
 *      görünen sekme yenilendiğinde gizli sekmenin de belirmesi gerekir.
 *   2) Bileşen bir `v-dialog` (NewFormDialog) içinde render ediliyor;
 *      Vuetify'nin Bootable mixin'i dialog kapansa da içeriği YOK ETMİYOR —
 *      bileşen YENİDEN OLUŞTURULMUYOR, yani mount-anı hesaplaması bir daha
 *      ÇALIŞMIYOR. Verinin `yenile()` DIŞINDA bir yoldan SONRADAN
 *      tamamlanması durumunda da sekme kendiliğinden belirmelidir.
 *
 * `veriVarMi` sekmeye özgü kuralı verir (varsayılan `gecerliSorguCevabiMi`);
 * emekli sekmelerinin DEV-3514 kuralları `BorcluSigortaBilgileri.sekmeVeriVarMi`de.
 */
export function sekmeGorunurSonraki(
    oncekiGorunur: boolean,
    cevap: any,
    veriVarMi: (cevap: any) => boolean = gecerliSorguCevabiMi
): boolean {
    return oncekiGorunur || veriVarMi(cevap);
}
